import qs from 'qs';
import Url from 'url-parse';

/**
 * 获取 url 中的参数，例如 `fxg.com?a=1&b=2`
 * 就会返回 `{ a: 1, b: 2 }`
 */
export const getUrlQueryParams = (
  url: string
): {
  [key: string]: string | undefined;
} => {
  if (!url) {
    return {};
  }
  const parseUrl = Url(url, true);
  const { query } = parseUrl;
  return query as Record<string, string | undefined>;
};

/**
 * 递归寻找btm dom节点
 */
export function findNode(
  dom: HTMLElement | null,
  btmArray: string[] = []
): { btmArray: string[]; dataBtmIdParent: string } {
  if (!dom) {
    // 考虑到业务方忘埋c位的情况，手动推入c0
    if (btmArray.length !== 0 && !btmArray.some((item) => /^c(\d+)$/.test(item))) {
      btmArray.push('c0');
    }
    return { btmArray, dataBtmIdParent: '' };
  }

  const hasDataBtmId = dom.getAttribute('data-btm-id');
  if (hasDataBtmId) {
    return { btmArray, dataBtmIdParent: hasDataBtmId };
  }

  const btmId = dom.getAttribute('data-btm');
  if (btmId) {
    // 如果现在数组里没有d点位信息，已经遍历到c位了，那么传入一个d0
    // 对c位的判断出了第一位是c还要考虑第二位是数字，不然d位为create时会导致判断错误
    if (/^c(\d+)$/.test(btmId) && btmArray.length === 0) {
      btmArray.push('d0');
    }
    // 如果是b111这种，默认为b位，则不推入数组（提醒业务方d位不要设置成b/c加数字的情况）
    if (!/^b(\d+)$/.test(btmId)) {
      btmArray.push(btmId);
    }
  }
  return findNode(dom?.parentElement, btmArray);
}

export const arrayEvery = <T>(array: T[], predicate: (value: T) => boolean): boolean => {
  const length = array === null ? 0 : array.length;
  let index = -1;
  while (++index < length) {
    if (!predicate(array[index])) {
      return false;
    }
  }
  return true;
};

/**
 * 递归寻找父节点的 BCM，只寻找到最近一个带有 BCM 参数的父节点
 */
export function findParentWithAttribute(element: HTMLElement | null | undefined): Record<string, unknown> {
  // 如果当前元素不存在，返回 null
  if (!element || !element.parentElement) {
    return {};
  }

  // 获取 data-btm-config 属性的值
  const dataBtmConfigValue = element.parentElement.getAttribute('data-btm-config');

  // 检查是否存在 data-btm-config 属性并且值是一个非空对象
  if (dataBtmConfigValue) {
    try {
      const configObject = JSON.parse(dataBtmConfigValue);
      // 检查对象是否包含 params 键且值不为空
      if (configObject.params && Object.keys(configObject.params).length > 0) {
        return configObject.params; // 返回当前元素
      }
    } catch (error) {
      // JSON 解析失败，表示 data-btm-config 不是一个合法的 JSON 对象
      console.error('Error parsing data-btm-config as JSON:', error);
    }
  }
  // 如果不符合条件，递归调用该函数，查找父节点
  return findParentWithAttribute(element.parentElement);
}

/**
 * 验证 btm id 是否符合 `${app}.${page}.${module}.${index}`
 * @param id btmid
 * @returns is legal or not
 */
export const validBtmId = (ids: string[]): boolean =>
  arrayEvery(ids, (val: string) => /^[0-9a-zA-Z_!~*()+$%-]+$/.test(val));

/**
 * 判断是否符合a.b.c.d点位数据
 */
export function getValidBtmId(btmStr: string) {
  const btmArr = btmStr.split('.');
  const defaultId = `${btmArr[0]}.${btmArr[1]}.c0.d0`;

  if (validBtmId(btmArr)) {
    return btmArr.join('.');
  }

  return defaultId;
}

/**
 * 设置 sessionStorage内容
 */
export function setSessionStorage(key: string, value: any) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {}
}

/**
 * 获取 sessionStorage内容
 */
export function getSessionStorage(key: string) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return '';
  }
}

/**
 * 对象转url参数
 * @param {Object} searchParams
 */
export function objectToQuery(searchParams: Record<string, any>) {
  return qs.stringify(searchParams);
}

/**
 * 区分是否是生产环境
 * 本地环境是 'undefined-undefined'
 * boe环境则是 'boe-0'
 * 线上环境是 'prod-0'
 * ppe环境的canary字段不确定，预期是一个数字，但可能为0，即'prod-0'或者'prod-111'
 */
export function getIsProd() {
  try {
    return `${window?.gfdatav1?.env || 'undefined'}-${window?.gfdatav1?.canary || 'undefined'}`;
  } catch (error) {
    console.error('btm-web-sdk error', error, window?.gfdatav1);
    return 'undefined-undefined';
  }
}

export function isDOM(obj: any) {
  return (
    obj &&
    typeof obj === 'object' &&
    obj.nodeType === 1 &&
    typeof obj.style === 'object' &&
    typeof obj.ownerDocument === 'object'
  );
}

export const svgTags = ['svg', 'path', 'g', 'image', 'text', 'line', 'rect', 'polygon', 'circle', 'ellipse'];

export const ignoreTags = ['html', 'body', ...svgTags];

export const getTagName = (ele: HTMLElement) => {
  const tag = ele.tagName.toLowerCase();
  if (svgTags.indexOf(tag) !== -1) {
    return `*[name()='${tag}']`;
  }
  return tag;
};

export function findIndex(ele: Element | null, currentTag: string) {
  let nth = 0;
  while (ele) {
    if (ele.nodeName.toLowerCase() === currentTag) {
      nth += 1;
    }
    ele = ele.previousElementSibling;
  }
  return nth;
}

export function getXpath(ele?: HTMLElement, allId = false) {
  if (!ele) {
    return null;
  }
  if (!isDOM(ele)) {
    return null;
  }

  let cur: HTMLElement | null = ele;
  let hasAddedId = false;
  const hasSvgEle = svgTags.indexOf(cur.tagName.toLowerCase()) !== -1;

  const path = [];
  try {
    while (cur && cur.nodeType === Node.ELEMENT_NODE) {
      const currentTag = cur.nodeName.toLowerCase();
      const nth = findIndex(cur, currentTag);
      let idMark = '';
      if (!hasAddedId && cur.hasAttribute('id')) {
        // 仅保留最近一层的id
        idMark = `[@id="${cur.id}"]`;
        hasAddedId = true;
      }
      let nthmark = '';
      if (idMark) {
        nthmark = '';
      } else if (ignoreTags.indexOf(currentTag) === -1) {
        nthmark = `[${nth}]`;
      } else {
        nthmark = nth === 1 ? '' : `[${nth}]`;
      }
      path.push(`${getTagName(cur)}${nthmark}${idMark}`);
      // svg元素会一直往上冒，由于 intersectionObser 对 svg 无效，所以需要获取完整的 xpath 来寻找最近的非 svg 元素
      if (idMark && !hasSvgEle && !allId) {
        path.push('');
        break;
      }
      cur = cur.parentNode as HTMLElement;
    }
  } catch {}
  return `/${path.reverse().join('/')}`;
}

export function safeStringfy(params: unknown) {
  if (typeof params === 'string') {
    return params;
  }
  let res = '';
  try {
    res = JSON.stringify(params);
  } catch {}
  return res;
}

export function getFullBtmDPoint(btm: string) {
  const arr = btm.split('.');
  return arr[arr.length - 1];
}

export function shouldReportClick(btmD: string, disableArr?: string[]) {
  if (!disableArr) return true;
  // 检查 btmD 是否在 disableArr 中
  if (disableArr.includes(btmD)) {
    return false;
  }

  // 检查 btmD 是否匹配 disableArr 中的模式
  for (let pattern of disableArr) {
    // 用正则表达式替换占位符 ${index} 为 \d+ 以匹配数字
    let regexPattern = pattern.replace('${index}', '\\d+');
    let regex = new RegExp(`^${regexPattern}$`);
    if (regex.test(btmD)) {
      return false;
    }
  }

  // 其他情况 shouldReport 为 true
  return true;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6IiIsIm5hbWVzIjpbXSwic291cmNlcyI6W10sInNvdXJjZXNDb250ZW50IjpbXX0=